import React, { useCallback, useContext, useState } from 'react';
import { useDispatch } from 'react-redux';

import { List } from 'antd';

import filesActions from '../../../actions';

import { message } from '../../../../../utils/message';

import DisplaySizeContext from '../../../../../context/DisplaySize';
import FileCard from '../FileCard';
import FileListItem from '../FileListItem';
import UploadFileModal from '../UploadFileModal';

import { AppDispatch } from '../../../../../types/app';
import { AiFileItem, AiFileStatus, FilesViewMode } from '../../../types';

type Props = {
  files: AiFileItem[];
  viewMode: FilesViewMode;
  selectedIds: number[];
  onSelectChange: (id: number, checked: boolean) => void;
  onAtlasIframeClose: () => void;
};

const getFileIconName = (file: AiFileItem) =>
  file.Status === AiFileStatus.Processed ? 'fileType-2' : 'fileType-1';

const FilesGrid = ({
  files,
  viewMode,
  selectedIds,
  onSelectChange,
  onAtlasIframeClose,
}: Props) => {
  const displaySize = useContext(DisplaySizeContext);
  const dispatch = useDispatch<AppDispatch>();
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [replaceFileId, setReplaceFileId] = useState<number | null>(null);
  const isCompactActions = displaySize !== 'desktop';

  const handleRemove = useCallback(
    async (id: number) => {
      if (removingId !== null) {
        return;
      }

      setRemovingId(id);

      try {
        await dispatch(filesActions.Files.removeFile(id));
      } catch (err) {
        console.error(err);
        message.error('files.remove.error');
      } finally {
        setRemovingId(null);
      }
    },
    [dispatch, removingId]
  );

  if (viewMode === 'list') {
    return (
      <>
        <List
          className="files-page__list"
          dataSource={files}
          renderItem={(file) => (
            <FileListItem
              key={file.Id}
              file={file}
              iconName={getFileIconName(file)}
              isCompactActions={isCompactActions}
              removingId={removingId}
              selectable
              selected={selectedIds.includes(file.Id)}
              onSelectChange={(checked) => onSelectChange(file.Id, checked)}
              onAtlasIframeClose={onAtlasIframeClose}
              onRemove={(id) => void handleRemove(id)}
              onReplace={setReplaceFileId}
            />
          )}
        />
        <UploadFileModal
          visible={replaceFileId !== null}
          fileId={replaceFileId ?? undefined}
          onClose={() => {
            setReplaceFileId(null);
          }}
        />
      </>
    );
  }

  return (
    <div className="files-page__grid">
      {files.map((file) => (
        <FileCard
          key={file.Id}
          file={file}
          iconName={getFileIconName(file)}
          selectable
          selected={selectedIds.includes(file.Id)}
          onSelectChange={(checked) => onSelectChange(file.Id, checked)}
          onAtlasIframeClose={onAtlasIframeClose}
        />
      ))}
    </div>
  );
};

export default FilesGrid;
